/**
 * ScrollerNumber Web Component
 * 
 * A custom element that displays the current slide position (e.g., "1/3")
 * Takes an attribute "for" that specifies the ID of the element to track
 */
class ScrollerNumber extends HTMLElement {
  constructor() {
    super();
    this.current = 1;
    this.total = 0;
    this.attachShadow({ mode: 'open' });
    this.render();
    
    // Element to observe
    this.targetElement = null;
    this.observer = null;
  }

  connectedCallback() {
    // Get the target element ID from the "for" attribute
    const targetId = this.getAttribute('for');
    if (targetId) {
      this.targetElement = document.getElementById(targetId);
      this.setupObserver();
    } else {
      console.error('ScrollerNumber: No target element specified (use "for" attribute)');
    }
  }

  disconnectedCallback() {
    // Clean up observer when element is removed
    if (this.observer) {
      this.observer.disconnect();
    }
  }

  setupObserver() {
    if (!this.targetElement) return;

    // Count total slides (ignoring style and script elements)
    const allChildren = Array.from(this.targetElement.children);
    const slides = allChildren.filter(child => 
      !['STYLE', 'SCRIPT'].includes(child.tagName));
    this.total = slides.length;

    // Don't set any custom width - respect the elements' natural width

    // Create intersection observer to detect visible slides
    const options = {
      root: this.targetElement,
      threshold: 0.5 // Element is considered visible when 50% in view
    };

    this.observer = new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          // Find the index of the visible slide
          const visibleElement = entry.target;
          const index = Array.from(slides).indexOf(visibleElement);
          if (index !== -1) {
            this.current = index + 1;
            this.render();
          }
        }
      });
    }, options);

    // Apply the required styles before observing slides
    this.ensureSlideStyles();
    
    // Observe all child elements (slides), ignoring style and script elements
    slides.forEach(slide => {
      this.observer.observe(slide);
    });

    // Initial render with correct total
    this.render();
  }

  // Handle scroll events on the target element
  handleScroll() {
    if (!this.targetElement) return;
    
    // Filter out style and script elements
    const allChildren = Array.from(this.targetElement.children);
    const slides = allChildren.filter(child => 
      !['STYLE', 'SCRIPT'].includes(child.tagName));
    
    if (slides.length === 0) return;
    
    const scrollLeft = this.targetElement.scrollLeft;
    const containerWidth = this.targetElement.offsetWidth;
    const containerCenter = scrollLeft + (containerWidth / 2);
    
    // Find the slide that is most centered in the view
    let closestSlide = null;
    let closestDistance = Infinity;
    
    slides.forEach((slide, index) => {
      // Get the center position of this slide
      const slideLeft = slide.offsetLeft;
      const slideWidth = slide.offsetWidth;
      const slideCenter = slideLeft + (slideWidth / 2);
      
      // Calculate distance from container center to slide center
      const distance = Math.abs(containerCenter - slideCenter);
      
      // If this slide is closer to center than previous best match
      if (distance < closestDistance) {
        closestDistance = distance;
        closestSlide = index;
      }
    });
    
    // Calculate current slide (1-based)
    const currentSlide = closestSlide !== null ? closestSlide + 1 : 1;
    
    if (currentSlide !== this.current && currentSlide <= this.total) {
      this.current = currentSlide;
      this.render();
    }
  }

  render() {
    const style = `
      :host {
        display: block;
        font-family: sans-serif;
        font-size: 14px;
        color: #000;
        padding: 8px 0;
      }
      
      /* No forced width styles */
    `;

    this.shadowRoot.innerHTML = `
      <style>${style}</style>
      <div>${this.current}/${this.total}</div>
    `;
    
    // Add styles to target element if needed
    if (this.targetElement) {
      // Add CSS to ensure slides take 4/5 of the width
      this.ensureSlideStyles();
    }
    
    // Add scroll event listener to the target element
    if (this.targetElement && !this._hasScrollListener) {
      this.targetElement.addEventListener('scroll', this.handleScroll.bind(this));
      this._hasScrollListener = true;
    }
  }
  
  // Apply the necessary styles for proper scrolling while respecting element widths
  ensureSlideStyles() {
    if (!this.targetElement) return;
    
    // Apply styles to the target element
    if (!this.targetElement._stylesApplied) {
      // Make sure the target element has the right styling
      this.targetElement.style.scrollSnapType = 'x mandatory';
      this.targetElement.style.scrollBehavior = 'smooth';
      this.targetElement.style.display = 'flex';
      this.targetElement.style.overflowX = 'auto';
      this.targetElement.style.scrollbarWidth = 'none'; // Hide scrollbar in Firefox
      
      // Add styles to hide scrollbar in WebKit browsers and ensure proper slide behavior
      const style = document.createElement('style');
      style.textContent = `
        #${this.targetElement.id}::-webkit-scrollbar {
          display: none;
        }
        
        #${this.targetElement.id} > *:not(style):not(script) {
          scroll-snap-align: center;
          box-sizing: border-box;
          /* No forced width - will use the elements' natural width */
          flex-shrink: 0; /* Prevent elements from shrinking */
        }
      `;
      document.head.appendChild(style);
      
      // Additionally, check if child elements need any styles applied directly
      const allChildren = Array.from(this.targetElement.children);
      const slides = allChildren.filter(child => 
        !['STYLE', 'SCRIPT'].includes(child.tagName));
      
      // Ensure slides maintain their natural width
      slides.forEach(slide => {
        // Only set minimal necessary styles to ensure proper scrolling behavior
        slide.style.flexShrink = '0'; // Prevent shrinking
      });
      
      this.targetElement._stylesApplied = true;
    }
  }

  // Handle attribute changes
  static get observedAttributes() {
    return ['for'];
  }
  
  attributeChangedCallback(name, oldValue, newValue) {
    if (name === 'for' && oldValue !== newValue) {
      // Disconnect old observer if it exists
      if (this.observer) {
        this.observer.disconnect();
      }
      
      // Setup new observer with new target
      this.targetElement = document.getElementById(newValue);
      this.setupObserver();
    }
  }
}

// Register the custom element
customElements.define('scroller-number', ScrollerNumber);
